import React, { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const History = () => {
  const containerRef = useRef(null);

  // --- DATA ---
  const milestones = [
    { year: "2011", title: "The Genesis", desc: "Vida Life Sciences Pvt Ltd. was launched by Dr. Onkar Yadav, Mr. Dhananjay Sawant & Mr Sanjay Khandagale.", badge: "Foundation" },
    { year: "2012", title: "Supply Operations", desc: "Started hospital equipment & furniture supply.", badge: "Operations Start" },
    { year: "2013", title: "Market Establishment", desc: "Started in-house manufacturing of CSSD, OT equipment and hospital furniture.", badge: "Manufacturing" },
    { year: "2014", title: "Biomedical Consultancy", desc: "Started designing new hospital project services & biomedical consultancy.", badge: "Consultancy" },
    { year: "2015", title: "Geographic Expansion", desc: "Vida brand reached almost every district of Maharashtra State along with projects in Karnataka, North region, Telangana, Madhya Pradesh, Gujarat, Muscat and Malaysia.", badge: "Global Expansion" },
    { year: "2016", title: "R&D Inception", desc: "Started its own Research & Development department to develop new CSSD equipment.", badge: "R&D Launch" },
    { year: "2017", title: "National Excellence", desc: "Vida was awarded the Fastest Growing Indian Company Excellence Award at International Achievers Conference", badge: "Awarded" },
    { year: "2018", title: "Tech Innovation", desc: "Vida Developed Automatic bed pan washer disinfector with its A0 value calculation.", badge: "Innovation" },
    { year: "2019", title: "Global Breakthrough", desc: "Vida got a breakthrough in 1st international CSSD set up in South Africa - Congo.", badge: "International Project" },
    { year: "2020", title: "Resilience", desc: "Vida has struggle a lot, coz of Covid effect, but still successfully done installation in Manipur- Imphal with huge challenges at American oncology centre.", badge: "Covid Resilience" },
    { year: "2021", title: "Govt. Partnerships", desc: "Vida moved on with many successful orders for CSSD in government sector too.", badge: "Government Projects" },
    { year: "2022", title: "Engineering", desc: "Vida has newly added double door instrument washer cum dryer.", badge: "New Product" },
    { year: "2023", title: "Scaling", desc: "Vida continuing with many CSSD projects including govt. Medical colleges.", badge: "Scale Up" },
    { year: "2024", title: "Validation", desc: "Vida Publish research article on A0 Concept in an international journal Scopus Indexed….and many products under development phases for ICU and Dental too…….", badge: "Research Published" },
    { year: "2025", title: "Leadership", desc: "1) Vida Lifesciences Pvt Ltd received its first patent! This is a significant milestone that highlights innovation and hard work. 2) ⁠Vida unveil the next generation of automated instrument care: The Vida’s Sensor-operated Instrument Washer-Cum-Dryer. 3) ⁠Vida introduced Advanced-Technology Automatic Vertical Sliding Door Steam Sterilizer. 4) Dr. Onkar Yadav, the Director of Vida, earned his doctorate in Healthcare Infection Control and Prevention. He also authored his first book, Invisible Defenders: Sterilization and Infection Control Behind the Scenes.", badge: "Patent & Leadership" }
  ];

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      
      // 1. Hero Text Reveal
      gsap.from(".hero-title", { y: 100, opacity: 0, duration: 1.5, ease: "power4.out" });
      gsap.from(".hero-sub", { y: 50, opacity: 0, duration: 1.5, delay: 0.3, ease: "power4.out" });

      // 2. Center Line Fill
      gsap.fromTo(".line-fill", 
        { height: "0%" },
        {
          height: "100%",
          ease: "none",
          scrollTrigger: {
            trigger: ".timeline-container",
            start: "top center",
            end: "bottom center",
            scrub: 1,
          }
        }
      );

      // 3. Floating Years & Cards
      gsap.utils.toArray(".timeline-row").forEach((row) => {
        const card = row.querySelector(".info-card");
        const year = row.querySelector(".bg-year");

        gsap.fromTo(card,
          { y: 100, opacity: 0 },
          {
            y: 0, 
            opacity: 1, 
            duration: 1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: row,
              start: "top 85%",
            }
          }
        );

        gsap.to(year, {
          y: -50,
          ease: "none",
          scrollTrigger: {
            trigger: row,
            start: "top bottom",
            end: "bottom top",
            scrub: true
          }
        });
      });

    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className="bg-slate-50 min-h-screen font-sans text-slate-900">
      
      {/* Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Manrope:wght@300;400;600&display=swap');
        .font-cinematic { font-family: 'Playfair Display', serif; }
        .font-body { font-family: 'Manrope', sans-serif; }
      `}</style>

      {/* ================= HERO SECTION (UPDATED SLOGAN) ================= */}
      {/* ================= HERO (CINEMATIC UPDATE) ================= */}
      <header className="relative h-[80vh] min-h-[600px] flex flex-col items-center justify-center text-center px-6 mb-20 overflow-hidden">
        
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
           <img 
             src="/hospital14.jpg" 
             alt="History Architecture" 
             className="w-full h-full object-cover"
           />
           {/* Cinematic Dark Overlay */}
           <div className="absolute inset-0 bg-gradient-to-b from-slate-700/80 via-slate-900/60"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto mt-20">
          <div className="hero-badge inline-block px-6 py-2 border border-white/20 rounded-full text-sky-300 font-bold text-sm tracking-[0.2em] uppercase mb-6 bg-white/5 backdrop-blur-md">
            Est. 2011
          </div>
          <h1 className="main-title text-5xl lg:text-8xl font-bold text-white leading-[0.9] tracking-tighter mb-8 drop-shadow-2xl">
            EVOLUTION OF <br/> 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-blue-500">
              DISCIPLINE
            </span>
          </h1>
          <p className="desc-text text-xl text-white max-w-2xl mx-auto leading-relaxed font-bold">
            Vida did not appear overnight. We are the result of 14 years of phased capability building, field-tested research, and validated maturity.
          </p>
        </div>
      </header>
      {/* ================= TIMELINE SECTION ================= */}
      <div className="timeline-container relative container mx-auto px-4 md:px-12 max-w-6xl pb-40">
        
        {/* Center Spine */}
        <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-[1px] bg-slate-200 -translate-x-1/2 md:translate-x-0 h-full">
           <div className="line-fill w-[3px] -ml-[1px] bg-gradient-to-b from-sky-400 via-blue-600 to-sky-400 shadow-[0_0_15px_rgba(14,165,233,0.3)] h-0"></div>
        </div>

        {/* Milestones */}
        <div className="flex flex-col gap-32 pt-12">
          {milestones.map((item, index) => {
            const isEven = index % 2 === 0;
            
            return (
              <div 
                key={index} 
                className={`timeline-row flex flex-col md:flex-row items-center w-full relative ${
                  isEven ? "md:flex-row-reverse" : ""
                }`}
              >
                
                {/* Content Card */}
                <div className="w-full md:w-[45%] pl-12 md:pl-0 md:px-12 mb-8 md:mb-0 relative z-10">
                  <div className="info-card bg-white p-10 rounded-xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] border border-slate-100 hover:border-sky-200 transition-colors duration-500 relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-1 h-full bg-sky-500 transform scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-top"></div>

                    <span className="font-body text-xs font-bold text-sky-600 tracking-widest uppercase mb-3 block">
                      {item.badge}
                    </span>
                    
                    {/* <h3 className="font-cinematic text-3xl text-slate-900 mb-4 italic">
                      {item.title}
                    </h3> */}
                    
                    <p className="font-body text-slate-600 leading-relaxed text-base font-light">
                      {item.desc}
                    </p>
                  </div>
                </div>

                {/* Dot */}
                <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-white border-[3px] border-sky-600 z-20 shadow-md"></div>

                {/* Background Year */}
                <div className={`w-full md:w-[45%] pl-12 md:pl-0 md:px-12 flex ${isEven ? "justify-start" : "justify-end"} relative z-0`}>
                   <h2 className="bg-year font-cinematic text-[6rem] md:text-[9rem] text-slate-200 leading-none select-none absolute top-1/2 -translate-y-1/2 pointer-events-none transition-colors duration-700">
                     {item.year}
                   </h2>
                </div>

              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default History;